import { api } from "./client";
import type { PushPrefs, PushPrefsUpdate, TokenResponse, User } from "@/types";

export const authApi = {
  /**
   * Login OAuth2 password flow.  FastAPI espera form-urlencoded
   * com `username` (email) e `password`.
   */
  async login(email: string, password: string): Promise<TokenResponse> {
    const params = new URLSearchParams();
    params.set("username", email);
    params.set("password", password);
    const { data } = await api.post<TokenResponse>("/auth/login", params, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });
    return data;
  },

  /**
   * Troca um token ainda válido por outro com validade cheia.
   *
   * É o que mantém o modo painel vivo: a tela pendurada na parede renova
   * sozinha antes de vencer, em vez de cair na página de login uma vez por
   * dia. Exige token válido — sessão que já morreu não ressuscita por aqui.
   */
  async renovar(): Promise<TokenResponse> {
    const { data } = await api.post<TokenResponse>("/auth/refresh");
    return data;
  },

  async me(): Promise<User> {
    const { data } = await api.get<User>("/auth/me");
    return data;
  },

  /** Preferência de push do próprio usuário. Ninguém altera a de outro. */
  async pushPrefs(): Promise<PushPrefs> {
    const { data } = await api.get<PushPrefs>("/users/me/push");
    return data;
  },

  async updatePushPrefs(patch: PushPrefsUpdate): Promise<PushPrefs> {
    const { data } = await api.patch<PushPrefs>("/users/me/push", patch);
    return data;
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await api.post("/users/me/password", {
      current_password: currentPassword,
      new_password: newPassword,
    });
  },

  /** Solicita link de redefinição de senha. Sempre resolve (mesmo se email não existir). */
  async forgotPassword(email: string): Promise<{ detail: string }> {
    const { data } = await api.post<{ detail: string }>("/auth/forgot-password", { email });
    return data;
  },

  /**
   * Diz se o link ainda serve, sem consumi-lo. Serve pra tela não abrir o
   * formulário de um link morto — antes ela só checava o TAMANHO do token,
   * então um link já usado abria o formulário e só falhava depois que a
   * pessoa digitava a senha nova.
   *
   * Devolve `true`, nunca `void`: é usado como queryFn, e no react-query v5
   * consulta que resolve com `undefined` VIRA ERRO — calado em produção. Com
   * `Promise<void>` todo link válido aparecia "Link inválido", de 10/09 a
   * 22/10/2026: nenhum convite nem redefinição de senha funcionou.
   */
  async verifyResetToken(token: string): Promise<true> {
    await api.post("/auth/reset-password/verify", { token });
    return true;
  },

  /** Consome o token e altera a senha. */
  async resetPassword(token: string, newPassword: string): Promise<{ detail: string }> {
    const { data } = await api.post<{ detail: string }>("/auth/reset-password", {
      token,
      new_password: newPassword,
    });
    return data;
  },
};
